import type { ItemStatus } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import type { IconName } from '../icons';
import { Icon } from './Icon';

// Inspector result pill (02 / 04): leading icon + the OFFICIAL result name from the glossary
// (Pass · Defect · Unable to Inspect · N/A; the non-result state shows Pending). The persisted
// identifiers (checkedOk …) never reach the UI — Authority §5.2.
export const RESULT_PRESENTATION: Record<ItemStatus, { labelKey: MessageKey; icon: IconName }> = Object.freeze({
  notChecked: { labelKey: 'result.pending', icon: 'pending' },
  checkedOk: { labelKey: 'result.pass', icon: 'check_circle' },
  issueFound: { labelKey: 'result.defect', icon: 'warning' },
  unableToInspect: { labelKey: 'result.unableToInspect', icon: 'visibility_off' },
  notApplicable: { labelKey: 'result.na', icon: 'do_not_disturb_on' }
});

export function StatusPill(props: { status: ItemStatus; small?: boolean; testId?: string }) {
  const { t } = useT();
  const presentation = RESULT_PRESENTATION[props.status];
  return (
    <span className={`pill pill-${props.status} ${props.small ? 'pill-small' : ''}`} data-status={props.status} data-testid={props.testId}>
      <Icon name={presentation.icon} />
      {t(presentation.labelKey)}
    </span>
  );
}

// Offline / local / sync status chip (Authority §13.3, D-10): presentation of a real state only;
// nothing here implies sync semantics (deferred to Batch 5, D-11).
export type StatusChipState = 'offline' | 'offlineSaved' | 'offlineLocal' | 'localOnly' | 'synced' | 'syncing' | 'syncPending' | 'syncFailed';

const STATUS_CHIP_KEYS: Record<StatusChipState, MessageKey> = Object.freeze({
  offline: 'status.offline',
  offlineSaved: 'status.offlineSaved',
  offlineLocal: 'status.offlineLocal',
  localOnly: 'status.localOnly',
  synced: 'status.synced',
  syncing: 'status.syncing',
  syncPending: 'status.syncPending',
  syncFailed: 'status.syncFailed'
});

const STATUS_CHIP_ICONS: Partial<Record<StatusChipState, IconName>> = Object.freeze({
  offline: 'wifi_off',
  offlineSaved: 'cloud_off',
  syncPending: 'sync',
  synced: 'cloud_done',
  syncing: 'sync',
  syncFailed: 'sync_problem'
});

export function StatusChip(props: { state: StatusChipState; count?: number; testId?: string }) {
  const { t } = useT();
  const icon = STATUS_CHIP_ICONS[props.state];
  return (
    <span className={`status-chip status-chip-${props.state}`} data-state={props.state} data-testid={props.testId ?? 'status-chip'}>
      {icon ? <Icon name={icon} /> : <span className="status-chip-dot" aria-hidden="true" />}
      {t(STATUS_CHIP_KEYS[props.state], { n: props.count ?? 0 })}
    </span>
  );
}
